import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { inr } from "@/lib/format";
import { PageHeader } from "@/components/pawkt/PageHeader";
import { Loading, Empty } from "@/components/pawkt/Empty";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { PieChart, ArrowLeft, Plus, Trash2, AlertTriangle, TrendingDown, TrendingUp, Lock, Boxes, LineChart, Info } from "lucide-react";
import { toast } from "sonner";

const PERIODS = [
  ["today", "Today"], ["yesterday", "Yesterday"], ["this_week", "This Week"], ["last_week", "Last Week"],
  ["this_month", "This Month"], ["last_month", "Last Month"],
];

const STATE_LABEL = {
  actual_available: ["Actual cost", "bg-emerald-100 text-emerald-700"],
  partial: ["Partly actual", "bg-amber-100 text-amber-700"],
  estimated_only: ["Estimated", "bg-sky-100 text-sky-700"],
  not_configured: ["Cost not configured", "bg-slate-100 text-slate-600"],
  insufficient_data: ["Insufficient data", "bg-slate-100 text-slate-500"],
};

const CONF_LABEL = {
  high: ["High confidence", "bg-emerald-100 text-emerald-700"],
  medium: ["Estimate", "bg-amber-100 text-amber-700"],
  low: ["Low confidence", "bg-rose-100 text-rose-700"],
  unavailable: ["Unavailable", "bg-slate-100 text-slate-500"],
};

const SEV = {
  high: "border-rose-200 bg-rose-50 text-rose-800",
  medium: "border-amber-200 bg-amber-50 text-amber-800",
  low: "border-slate-200 bg-slate-50 text-slate-600",
};

const pct = (v) => (v === null || v === undefined ? "—" : `${v}%`);
const money = (v) => (v === null || v === undefined ? "—" : inr(v));
const qty = (v, u) => (v === null || v === undefined ? "—" : `${(+v).toLocaleString()}${u ? " " + u : ""}`);
const delta = (v) => (v === null || v === undefined ? "—" : `${v > 0 ? "+" : ""}${v}%`);

function StateBadge({ state }) {
  const [label, cls] = STATE_LABEL[state] || ["—", "bg-slate-100 text-slate-600"];
  return <span data-testid="cost-state-badge" className={`inline-block rounded-full px-2 py-0.5 text-[11px] font-medium ${cls}`}>{label}</span>;
}

function ConfBadge({ c, testid }) {
  const [label, cls] = CONF_LABEL[c] || ["—", "bg-slate-100 text-slate-600"];
  return <span data-testid={testid} className={`inline-block rounded-full px-2 py-0.5 text-[11px] font-medium ${cls}`}>{label}</span>;
}

function FeatureError({ error }) {
  const denied = error?.response?.status === 403;
  return <Empty icon={denied ? Lock : AlertTriangle}
    title={denied ? "Not available" : "Couldn't load profitability"}
    subtitle={denied ? "Service Profitability isn't enabled for your business. Contact your account administrator to request access." : "Something went wrong loading this data. Please try again."} />;
}

export default function ServiceProfitability() {
  const [sp] = useSearchParams();
  const serviceId = sp.get("service");
  const [period, setPeriod] = useState("this_month");
  const [tab, setTab] = useState("overview");
  return (
    <div>
      <PageHeader title="Service Profitability" subtitle="Margins, consumable cost & profit from your operational records">
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="w-40" data-testid="sp-period"><SelectValue /></SelectTrigger>
          <SelectContent>{PERIODS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
        </Select>
      </PageHeader>
      {serviceId ? (
        <Detail serviceId={serviceId} period={period} />
      ) : (
        <>
          <div className="mb-5 inline-flex rounded-lg border border-slate-200 bg-white p-0.5" data-testid="sp-tabs">
            <TabBtn active={tab === "overview"} onClick={() => setTab("overview")} testid="sp-tab-overview">Overview</TabBtn>
            <TabBtn active={tab === "monthly"} onClick={() => setTab("monthly")} testid="sp-tab-monthly">Monthly Intelligence</TabBtn>
          </div>
          {tab === "overview" ? <DashboardView period={period} /> : <MonthlyIntelligence period={period} />}
        </>
      )}
    </div>
  );
}

function TabBtn({ active, onClick, children, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`rounded-md px-4 py-1.5 text-sm font-medium transition-colors ${active ? "bg-slate-900 text-white" : "text-slate-600 hover:text-slate-900"}`}>
      {children}
    </button>
  );
}

function DashboardView({ period }) {
  const navigate = useNavigate();
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["svc-profit-dash", period],
    queryFn: async () => (await api.get(`/services/profitability?period=${period}`)).data,
    retry: false,
  });
  if (isLoading) return <Loading />;
  if (isError) return <FeatureError error={error} />;
  const s = data?.summary; const r = data?.rankings;
  if (!s) return <Empty icon={PieChart} title="No data" subtitle="No service activity in this period." />;
  const open = (id) => navigate(`/service-profitability?service=${id}`);
  const Card = ({ label, value, testid, sub }) => (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid={testid}>
      <div className="text-[11px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className="mt-1 text-xl font-semibold text-slate-900 tabular">{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
  const List = ({ title, rows, metric, icon: Icon, testid }) => (
    <div className="rounded-xl border border-slate-200 bg-white" data-testid={testid}>
      <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-100 text-sm font-semibold text-slate-800">
        {Icon && <Icon className="h-4 w-4 text-slate-400" />} {title}
      </div>
      {rows?.length ? (
        <ul className="divide-y divide-slate-50">
          {rows.map((x) => (
            <li key={x.service_id || x.name}>
              <button onClick={() => x.service_id && open(x.service_id)} data-testid={`sp-rank-${x.service_id || x.name}`}
                className="w-full flex items-center justify-between px-4 py-2.5 text-sm hover:bg-slate-50 text-left">
                <span className="truncate text-slate-700">{x.name}<span className="text-slate-400"> · {x.units}u</span></span>
                <span className="font-medium text-slate-900 tabular shrink-0 ml-3">{metric(x)}</span>
              </button>
            </li>
          ))}
        </ul>
      ) : <div className="px-4 py-6 text-center text-sm text-slate-400">Not enough data</div>}
    </div>
  );
  return (
    <div className="space-y-6">
      {s.services_with_incomplete_cost_config > 0 && (
        <div data-testid="sp-coverage-banner" className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
          <span><b>{s.services_with_incomplete_cost_config}</b> of {s.services_total} services have no consumable recipe configured — their profit is shown as unavailable, never assumed ₹0. Actual-cost coverage this period: <b>{s.cost_coverage_pct}%</b>.</span>
        </div>
      )}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card label="Service Revenue" value={money(s.service_revenue)} testid="sp-rev" />
        <Card label="Materials & medicines cost" value={money((s.actual_consumable_cost || 0) + (s.estimated_consumable_cost || 0))} sub={`${money(s.actual_consumable_cost)} actual · ${money(s.estimated_consumable_cost)} est`} testid="sp-cost" />
        <Card label="Profit after direct service costs" value={money(s.gross_profit)} sub={pct(s.gross_margin_pct) + " of revenue"} testid="sp-gp" />
        <Card label="Contribution Profit" value={money(s.contribution_profit)} sub="after commission" testid="sp-contrib" />
      </div>

      <ConsumptionRollup period={period} open={open} />

      <div className="grid md:grid-cols-2 gap-4">
        <List title="Most profitable" rows={r.most_profitable} metric={(x) => money(x.gross_profit)} icon={TrendingUp} testid="sp-most-profitable" />
        <List title="Lowest margin" rows={r.lowest_margin} metric={(x) => pct(x.gross_margin_pct)} icon={TrendingDown} testid="sp-lowest-margin" />
        <List title="Negative gross profit" rows={r.negative_gross_profit} metric={(x) => money(x.gross_profit)} icon={AlertTriangle} testid="sp-negative" />
        <List title="Below target margin (high volume)" rows={r.below_target_high_volume} metric={(x) => `${pct(x.gross_margin_pct)} / ${pct(x.target_margin_pct)}`} icon={TrendingDown} testid="sp-below-target" />
      </div>
      <AllServices rows={data.services} open={open} />
    </div>
  );
}

function ConsumptionRollup({ period, open }) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["svc-consumption-rollup", period],
    queryFn: async () => (await api.get(`/services/consumption-rollup?period=${period}`)).data,
    retry: false,
  });
  if (isLoading) return <div className="rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-400" data-testid="sp-rollup-loading">Loading consumption…</div>;
  if (isError || !data) return null;
  const s = data.summary;
  const Card = ({ label, value, testid, sub }) => (
    <div className="rounded-lg border border-slate-100 bg-slate-50 p-3" data-testid={testid}>
      <div className="text-[11px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className="mt-0.5 text-lg font-semibold text-slate-900 tabular">{value}</div>
      {sub && <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="sp-consumption-rollup">
      <div className="flex items-center gap-2 mb-3">
        <Boxes className="h-4 w-4 text-slate-400" />
        <h3 className="font-semibold text-slate-900">Consumable consumption · this period</h3>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card label="Expected (recipes)" value={money(s.total_expected_cost)} testid="sp-rollup-expected" sub="from completed service units" />
        <Card label="Explicit internal + service" value={money((s.total_internal_cost || 0) + (s.total_service_consumption_cost || 0))} testid="sp-rollup-explicit" sub="recorded in the ledger" />
        <Card label="Ledger-inferred net" value={money(s.total_inferred_cost)} testid="sp-rollup-inferred" sub="from stock movement" />
        <Card label="Unexplained variance" value={money(s.total_unexplained_variance_cost)} testid="sp-rollup-variance" sub="inferred − recorded" />
      </div>
      {(s.incomplete_products > 0 || s.not_tracked_products > 0) && (
        <div className="mt-3 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] text-amber-800" data-testid="sp-rollup-note">
          <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
          <span>
            {s.incomplete_products > 0 && <><b>{s.incomplete_products}</b> item(s) have incomplete stock history — their figures are estimates, not reconciled. </>}
            {s.not_tracked_products > 0 && <><b>{s.not_tracked_products}</b> item(s) had transfers/supplier-returns, which are <b>Not tracked / data unavailable</b> (never counted as a verified zero).</>}
          </span>
        </div>
      )}
      {data.top_products?.length > 0 && (
        <div className="mt-4 overflow-x-auto">
          <table className="w-full min-w-[560px] text-sm" data-testid="sp-rollup-table">
            <thead className="text-left text-xs uppercase tracking-wide text-slate-500">
              <tr><th className="py-2">Item</th><th className="py-2 text-right">Expected</th><th className="py-2 text-right">Inferred</th><th className="py-2 text-right">Variance</th><th className="py-2 text-right">Confidence</th></tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {data.top_products.map((p) => (
                <tr key={p.product_id} data-testid={`sp-rollup-row-${p.product_id}`}>
                  <td className="py-2 text-slate-800">{p.product_name}{!p.complete && <span className="text-[10px] text-amber-600"> · incomplete</span>}</td>
                  <td className="py-2 text-right tabular text-slate-600">{money(p.expected_cost_estimated)}</td>
                  <td className="py-2 text-right tabular text-slate-900">{p.complete ? money(p.inferred_cost_estimated) : "—"}</td>
                  <td className="py-2 text-right tabular text-slate-600">{p.complete ? money(p.unexplained_variance_cost) : "—"}</td>
                  <td className="py-2 text-right"><ConfBadge c={p.confidence} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function AllServices({ rows, open }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white overflow-x-auto" data-testid="sp-all-services">
      <table className="w-full min-w-[720px] text-sm">
        <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
          <tr><th className="px-4 py-3">Service</th><th className="px-4 py-3 text-right">Appts</th><th className="px-4 py-3 text-right">Units</th><th className="px-4 py-3 text-right">Revenue</th><th className="px-4 py-3 text-right">Cost</th><th className="px-4 py-3 text-right">Profit</th><th className="px-4 py-3 text-right">Profit %</th><th className="px-4 py-3">Cost basis</th></tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rows.map((r) => (
            <tr key={r.service_id || r.name} className="hover:bg-slate-50 cursor-pointer" onClick={() => r.service_id && open(r.service_id)} data-testid={`sp-row-${r.service_id || r.name}`}>
              <td className="px-4 py-2.5 font-medium text-slate-900">{r.name}</td>
              <td className="px-4 py-2.5 text-right tabular text-slate-600">{r.appointments}</td>
              <td className="px-4 py-2.5 text-right tabular text-slate-600">{r.units}</td>
              <td className="px-4 py-2.5 text-right tabular text-slate-900">{money(r.net_revenue)}</td>
              <td className="px-4 py-2.5 text-right tabular text-slate-600">{money(r.actual_cost ?? r.estimated_cost)}</td>
              <td className="px-4 py-2.5 text-right tabular font-semibold text-slate-900">{money(r.gross_profit)}</td>
              <td className="px-4 py-2.5 text-right tabular text-slate-600">{pct(r.gross_margin_pct)}</td>
              <td className="px-4 py-2.5"><StateBadge state={r.cost_state} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Detail({ serviceId, period }) {
  const navigate = useNavigate();
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["svc-profit-detail", serviceId, period],
    queryFn: async () => (await api.get(`/services/${serviceId}/profitability?period=${period}`)).data,
    retry: false,
  });
  if (isLoading) return <Loading />;
  if (isError) return <FeatureError error={error} />;
  if (!data) return <Empty icon={PieChart} title="Service not found" subtitle="This service may have been removed." />;
  const { service, margin, recipe, performance } = data;
  const sPrice = margin.suggested_price, mCost = margin.cost;
  const recProfit = (sPrice != null && mCost != null) ? Math.round((sPrice - mCost) * 100) / 100 : null;
  const recProfitPct = (sPrice != null && mCost != null && sPrice > 0) ? Math.round((sPrice - mCost) / sPrice * 1000) / 10 : null;
  const Row = ({ k, v, testid }) => (
    <div className="flex justify-between gap-3 py-1.5 text-sm"><span className="text-slate-500">{k}</span><span className="font-medium text-slate-900 tabular" data-testid={testid}>{v}</span></div>
  );
  return (
    <div className="space-y-5">
      <button onClick={() => navigate("/service-profitability")} data-testid="sp-back" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900"><ArrowLeft className="h-4 w-4" /> All services</button>
      <div className="grid md:grid-cols-2 gap-4">
        <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="sp-overview">
          <h3 className="font-semibold text-slate-900">{service.name}</h3>
          <div className="text-xs text-slate-500 capitalize mb-3">{service.category}</div>
          <Row k="Selling price" v={money(service.price)} />
          <Row k="Expected cost to provide this service" v={money(recipe.est_cost_per_unit)} testid="sp-est-cost-unit" />
          <Row k="Actual cost to provide this service" v={money(performance.avg_cost_per_unit)} testid="sp-actual-cost-unit" />
          <Row k="Profit as a percentage of selling price" v={pct(margin.achieved_margin_pct)} testid="sp-achieved-margin" />
          <div className="mt-2"><StateBadge state={performance.cost_state} /></div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="sp-target">
          <h3 className="font-semibold text-slate-900 mb-1">Your profit target</h3>
          <p className="text-[11px] leading-snug text-slate-500 mb-3">Choose a fixed rupee profit per service, or a percentage of the selling price — we'll recommend a price to hit that target.</p>
          <Row k="Profit target" v={service.margin_target_type ? (service.margin_target_type === "percent" ? `${service.margin_target_value}% of selling price` : `₹${service.margin_target_value} per service`) : "Not set"} />
          {margin.suggested_price !== null ? (
            <div className="mt-3 rounded-lg border border-slate-100 bg-slate-50 p-3 space-y-1" data-testid="sp-price-breakdown">
              <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">Recommended pricing{performance.cost_state !== "actual_available" ? " (estimate)" : ""}</div>
              <Row k="Expected cost to provide this service" v={money(margin.cost)} />
              <Row k="Recommended selling price" v={money(margin.suggested_price)} testid="sp-suggested-price" />
              <Row k="Expected profit per service" v={money(recProfit)} testid="sp-expected-profit" />
              <Row k="Expected profit percentage" v={pct(recProfitPct)} testid="sp-expected-profit-pct" />
            </div>
          ) : (
            <Row k="Recommended selling price" v="—" testid="sp-suggested-price" />
          )}
          {margin.error && <div className="mt-2 text-xs text-amber-700" data-testid="sp-margin-error">{margin.error}</div>}
          <div className="mt-2 text-[11px] text-slate-400">This is profit as a percentage of the selling price (not markup). Edit pricing on the Services page — we never change your price automatically.</div>
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="sp-performance">
        <h3 className="font-semibold text-slate-900 mb-3">Performance · this period</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-y-1 gap-x-6">
          <Row k="Completed appts" v={performance.appointments} testid="sp-appointments" />
          <Row k="Service units" v={performance.units} testid="sp-units" />
          <Row k="Revenue" v={money(performance.net_revenue)} testid="sp-net-revenue" />
          <Row k="Discounts" v={money(performance.discount)} />
          <Row k="Expected cost to provide this service" v={money(performance.estimated_cost)} />
          <Row k="Actual cost to provide this service" v={money(performance.actual_cost)} />
          <Row k="Profit after direct service costs (expected)" v={money(performance.estimated_gross_profit)} testid="sp-est-gp" />
          <Row k="Profit after direct service costs (actual)" v={money(performance.actual_gross_profit)} testid="sp-actual-gp" />
          <Row k="Profit as a percentage of selling price" v={pct(performance.gross_margin_pct)} />
          <Row k="Commission" v={money(performance.commission)} />
          <Row k="Contribution profit" v={money(performance.contribution_profit)} testid="sp-contribution" />
          <Row k="Actual coverage" v={pct(performance.actual_coverage_pct)} />
        </div>
      </div>

      <ServiceConsumption serviceId={serviceId} period={period} />

      <RecipeEditor serviceId={serviceId} recipe={recipe} />
    </div>
  );
}

function ServiceConsumption({ serviceId, period }) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["svc-consumption", serviceId, period],
    queryFn: async () => (await api.get(`/services/${serviceId}/consumption?period=${period}`)).data,
    retry: false,
  });
  if (isLoading) return <div className="rounded-xl border border-slate-200 bg-white p-5 text-sm text-slate-400" data-testid="sp-consumption-loading">Loading consumption…</div>;
  if (isError || !data) return null;
  const s = data.summary;
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="sp-consumption-block">
      <div className="flex items-center gap-2 mb-1">
        <Boxes className="h-4 w-4 text-slate-400" />
        <h3 className="font-semibold text-slate-900">Consumable consumption · {data.units} completed unit(s)</h3>
      </div>
      <p className="text-[11px] leading-snug text-slate-500 mb-3">
        Expected (from recipe), explicitly recorded internal consumption, and ledger-inferred net usage are shown as distinct measures and never summed. Ledger-inferred is a product-level figure, not attributed to this service unless a recorded consumption is reliably linked.
      </p>
      {data.missing_recipe && (
        <div className="mb-3 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] text-amber-800" data-testid="sp-consumption-missing-recipe">
          <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" /> No consumable recipe configured — expected usage is unavailable, never assumed ₹0.
        </div>
      )}
      {data.items?.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px] text-sm" data-testid="sp-consumption-table">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-3 py-2.5">Item</th>
                <th className="px-3 py-2.5 text-right">Expected</th>
                <th className="px-3 py-2.5 text-right">Explicit internal</th>
                <th className="px-3 py-2.5 text-right">Ledger-inferred (item)</th>
                <th className="px-3 py-2.5 text-right">Variance (exp vs recorded)</th>
                <th className="px-3 py-2.5 text-right">Confidence</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.items.map((it) => (
                <tr key={it.product_id} data-testid={`sp-consumption-row-${it.product_id}`}>
                  <td className="px-3 py-2.5 font-medium text-slate-900">{it.product_name}{!it.in_recipe && <span className="text-[10px] text-slate-400"> · not in recipe</span>}</td>
                  <td className="px-3 py-2.5 text-right tabular text-slate-600">{qty(it.expected_qty, it.unit)}<div className="text-[10px] text-slate-400">{money(it.expected_cost_estimated)}</div></td>
                  <td className="px-3 py-2.5 text-right tabular text-slate-900">{qty(it.explicit_internal_qty, it.unit)}<div className="text-[10px] text-slate-400">{money(it.explicit_internal_cost)}</div></td>
                  <td className="px-3 py-2.5 text-right tabular text-slate-600">{it.ledger_inferred_net === null || it.ledger_inferred_net === undefined ? "—" : qty(it.ledger_inferred_net, it.unit)}</td>
                  <td className="px-3 py-2.5 text-right tabular text-slate-600">{qty(it.variance_expected_vs_explicit, it.unit)}</td>
                  <td className="px-3 py-2.5 text-right"><ConfBadge c={it.confidence} testid={`sp-consumption-conf-${it.product_id}`} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-sm text-slate-400" data-testid="sp-consumption-empty">No consumable items or recorded consumption for this service in this period.</div>
      )}
      {data.notes?.length > 0 && (
        <ul className="mt-3 space-y-1 text-[11px] text-slate-500 list-disc pl-4" data-testid="sp-consumption-notes">
          {data.notes.map((n, i) => <li key={i}>{n}</li>)}
        </ul>
      )}
    </div>
  );
}

function MonthlyIntelligence({ period }) {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["consumption-monthly", period],
    queryFn: async () => (await api.get(`/inventory/consumption-monthly?period=${period}`)).data,
    retry: false,
  });
  if (isLoading) return <Loading />;
  if (isError) return <FeatureError error={error} />;
  if (!data) return <Empty icon={LineChart} title="No data" subtitle="No consumption activity to analyse." />;
  const { summary: sm, products, forecasts, alerts } = data;
  const cmp = data.period?.comparison || "previous period";
  const Card = ({ label, cur, prev, deltaV, testid }) => (
    <div className="rounded-lg border border-slate-100 bg-white p-4" data-testid={testid}>
      <div className="text-[11px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className="mt-0.5 text-lg font-semibold text-slate-900 tabular">{cur}</div>
      <div className="text-[11px] text-slate-500 mt-0.5">vs {cmp}: {prev}{deltaV !== undefined && deltaV !== null && <span className={deltaV > 0 ? "text-rose-600" : "text-emerald-600"}> ({delta(deltaV)})</span>}</div>
    </div>
  );
  return (
    <div className="space-y-6" data-testid="sp-monthly">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card label="Consumption cost" cur={money(sm.cur.consumption_cost)} prev={money(sm.prev.consumption_cost)} deltaV={sm.deltas.consumption_cost_pct} testid="mi-consumption" />
        <Card label="Purchases" cur={money(sm.cur.purchases_cost)} prev={money(sm.prev.purchases_cost)} deltaV={sm.deltas.purchases_cost_pct} testid="mi-purchases" />
        <Card label="Closing stock value" cur={money(sm.cur.closing_stock_value)} prev={money(sm.prev.closing_stock_value)} deltaV={sm.deltas.closing_stock_value_pct} testid="mi-closing" />
        <Card label="Inferred cost / service unit" cur={money(sm.cur.inferred_per_unit)} prev={money(sm.prev.inferred_per_unit)} testid="mi-per-unit" />
      </div>

      <div className="rounded-xl border border-slate-200 bg-white" data-testid="mi-alerts">
        <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-100 text-sm font-semibold text-slate-800">
          <AlertTriangle className="h-4 w-4 text-slate-400" /> Alerts
        </div>
        {alerts?.length ? (
          <ul className="p-3 space-y-2">
            {alerts.map((a, i) => (
              <li key={i} className={`flex items-start gap-2 rounded-lg border px-3 py-2 text-[12px] ${SEV[a.severity] || SEV.low}`} data-testid={`mi-alert-${a.type}-${i}`}>
                <span className="font-semibold uppercase text-[10px] mt-0.5 shrink-0">{a.type.replace(/_/g, " ")}</span>
                <span>{a.message}</span>
              </li>
            ))}
          </ul>
        ) : <div className="px-4 py-6 text-center text-sm text-slate-400" data-testid="mi-alerts-empty">No alerts — nothing unusual detected.</div>}
      </div>

      <div className="rounded-xl border border-slate-200 bg-white" data-testid="mi-forecasts">
        <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-100 text-sm font-semibold text-slate-800">
          <LineChart className="h-4 w-4 text-slate-400" /> Stock run-out forecast
        </div>
        <p className="px-4 pt-2 text-[11px] text-slate-500">Forecasts are shown only where there is enough reliable history and a valid stock balance. Each one discloses its assumptions and confidence; missing data is shown as <b>Unavailable</b>, never a fabricated estimate.</p>
        <div className="overflow-x-auto p-2">
          <table className="w-full min-w-[720px] text-sm" data-testid="mi-forecast-table">
            <thead className="text-left text-xs uppercase tracking-wide text-slate-500">
              <tr><th className="px-3 py-2">Item</th><th className="px-3 py-2 text-right">Current stock</th><th className="px-3 py-2 text-right">Daily rate</th><th className="px-3 py-2 text-right">Runs out in</th><th className="px-3 py-2">Confidence</th><th className="px-3 py-2">Assumptions</th></tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {forecasts.map((f) => (
                <tr key={f.product_id} data-testid={`mi-forecast-${f.product_id}`} className={f.state === "shortage" ? "bg-rose-50" : ""}>
                  <td className="px-3 py-2.5 font-medium text-slate-900">{f.product_name}</td>
                  <td className="px-3 py-2.5 text-right tabular text-slate-600">{qty(f.current_stock, f.unit)}</td>
                  <td className="px-3 py-2.5 text-right tabular text-slate-600">{f.daily_rate === null ? "—" : `${f.daily_rate}/d`}</td>
                  <td className="px-3 py-2.5 text-right tabular text-slate-900">{f.days_to_runout === null ? "—" : `${f.days_to_runout}d · ${f.runout_date}`}</td>
                  <td className="px-3 py-2.5"><ConfBadge c={f.confidence} testid={`mi-forecast-conf-${f.product_id}`} /></td>
                  <td className="px-3 py-2.5 text-[11px] text-slate-500 max-w-[260px]">{(f.assumptions || []).join(" · ")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white overflow-x-auto" data-testid="mi-products">
        <table className="w-full min-w-[760px] text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
            <tr><th className="px-4 py-3">Item</th><th className="px-4 py-3 text-right">Purchases</th><th className="px-4 py-3 text-right">Consumption</th><th className="px-4 py-3 text-right">Closing</th><th className="px-4 py-3 text-right">Inferred (now)</th><th className="px-4 py-3 text-right">vs {cmp}</th><th className="px-4 py-3">Confidence</th></tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {products.map((p) => (
              <tr key={p.product_id} data-testid={`mi-product-${p.product_id}`}>
                <td className="px-4 py-2.5 font-medium text-slate-900">{p.product_name}</td>
                <td className="px-4 py-2.5 text-right tabular text-slate-600">{qty(p.cur.purchases, p.unit)}</td>
                <td className="px-4 py-2.5 text-right tabular text-slate-600">{qty(p.cur.consumption, p.unit)}</td>
                <td className="px-4 py-2.5 text-right tabular text-slate-600">{qty(p.cur.closing, p.unit)}</td>
                <td className="px-4 py-2.5 text-right tabular text-slate-900">{qty(p.cur.inferred, p.unit)}</td>
                <td className="px-4 py-2.5 text-right tabular text-slate-600">{delta(p.inferred_delta_pct)}</td>
                <td className="px-4 py-2.5"><ConfBadge c={p.confidence} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function RecipeEditor({ serviceId, recipe }) {
  const qc = useQueryClient();
  const [lines, setLines] = useState(() => recipe.lines.map((l) => ({ product_id: l.product_id, qty: l.qty })));
  const { data: products } = useQuery({ queryKey: ["products-min"], queryFn: async () => (await api.get("/products")).data });
  const save = useMutation({
    mutationFn: () => api.put(`/services/${serviceId}/recipe`, { lines: lines.filter((l) => l.product_id && l.qty > 0) }),
    onSuccess: () => { toast.success("Recipe saved"); qc.invalidateQueries({ queryKey: ["svc-profit-detail"] }); qc.invalidateQueries({ queryKey: ["svc-consumption"] }); },
    onError: (e) => toast.error(apiErr(e)),
  });
  const prodName = (id) => (products || []).find((p) => p.id === id)?.name || "";
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="sp-recipe">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-slate-900">Materials and medicines used</h3>
        <Button size="sm" variant="outline" onClick={() => setLines([...lines, { product_id: "", qty: 1 }])} data-testid="sp-recipe-add"><Plus className="h-3.5 w-3.5 mr-1" /> Add item</Button>
      </div>
      {lines.length === 0 && <div className="text-sm text-slate-400 mb-3" data-testid="sp-recipe-empty">Consumable cost not configured. Add the inventory items used by one unit of this service.</div>}
      <div className="space-y-2">
        {lines.map((l, i) => (
          <div key={i} className="flex items-center gap-2" data-testid={`sp-recipe-line-${i}`}>
            <Select value={l.product_id} onValueChange={(v) => setLines(lines.map((x, j) => j === i ? { ...x, product_id: v } : x))}>
              <SelectTrigger className="flex-1" data-testid={`sp-recipe-product-${i}`}><SelectValue placeholder="Select inventory item">{prodName(l.product_id)}</SelectValue></SelectTrigger>
              <SelectContent>{(products || []).map((p) => <SelectItem key={p.id} value={p.id}>{p.name}{p.purchase_price ? ` · ${inr(p.purchase_price)}` : " · cost unknown"}</SelectItem>)}</SelectContent>
            </Select>
            <input type="number" min="0" step="0.01" value={l.qty} onChange={(e) => setLines(lines.map((x, j) => j === i ? { ...x, qty: parseFloat(e.target.value) || 0 } : x))}
              className="w-24 rounded-md border border-slate-200 px-2 py-1.5 text-sm" data-testid={`sp-recipe-qty-${i}`} />
            <button onClick={() => setLines(lines.filter((_, j) => j !== i))} data-testid={`sp-recipe-del-${i}`} className="p-1.5 text-rose-500 hover:bg-rose-50 rounded-md"><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
      </div>
      <div className="mt-4 flex items-center justify-between">
        <span className="text-sm text-slate-500">Expected cost to provide this service: <b className="text-slate-900">{money(recipe.est_cost_per_unit)}</b>{!recipe.all_cost_known && recipe.configured && <span className="text-amber-600"> (some item costs unknown)</span>}</span>
        <Button onClick={() => save.mutate()} disabled={save.isPending} data-testid="sp-recipe-save" className="bg-slate-900 text-white">{save.isPending ? "Saving…" : "Save recipe"}</Button>
      </div>
    </div>
  );
}
